// Stale-bundle notice.
//
// The failure this exists for: a phone tab left open for days keeps running
// whatever JS bundle it loaded on the first visit. The user sees bugs that
// were fixed a week ago and there's no signal that a reload would fix it.
//
// Detection is a filename comparison, not a version number. CRA content-hashes
// main.<hash>.js, so the basename of the <script> tag that booted this session
// versus the basename in the currently-served /asset-manifest.json is an exact
// answer to "am I running the deployed build?".
//
// Two hard rules:
//   1. NEVER auto-reload. A reload mid-draft would destroy in-progress state.
//      This offers a button; the user chooses when.
//   2. NEVER surface a failure. No manifest, offline, malformed JSON, CSP —
//      every path is swallowed. A stale-check that shows an error is strictly
//      worse than no stale-check.
//
// Dev has no asset-manifest.json and no hashed main.<hash>.js, so the missing
// script tag is the dev-server detection: bail before the fetch.

import { useEffect, useRef, useState } from "react";
import theme from "../data/theme";
import {
  captureUpdateDetected,
  captureUpdatePrompted,
  captureUpdateReloaded,
} from "../utils/updateAnalytics";

// Minimum gap between checks. visibilitychange fires on every tab focus and
// on some platforms on scroll-driven chrome changes; without this a user
// flipping between apps would hammer the manifest.
const CHECK_INTERVAL_MS = 60000;

// Compare filenames only — the running script is an absolute URL, the manifest
// entry is a root-relative path, and neither the origin nor a ?query is part
// of the identity we care about.
function basename(src) {
  if (!src) return null;
  const path = String(src).split(/[?#]/)[0];
  const name = path.substring(path.lastIndexOf("/") + 1);
  return name || null;
}

export default function UpdateBanner({ suppressed = false }) {
  // Holds the two basenames rather than a bare boolean: staleness IS the pair,
  // and both events report it.
  const [bundles, setBundles] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const lastCheckRef = useRef(0);
  const detectedRef = useRef(false);
  const promptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const checkFreshness = async () => {
      const now = Date.now();
      if (now - lastCheckRef.current < CHECK_INTERVAL_MS) return;
      lastCheckRef.current = now;

      try {
        const runningTag = document.querySelector('script[src*="/static/js/main."]');
        const running = basename(runningTag?.src);
        // No hashed bundle in the DOM: dev server. Nothing to compare against.
        if (!running) return;

        // no-store, or the browser happily answers from the same stale cache
        // that gave us the stale bundle in the first place.
        const res = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!res.ok) return;
        const json = await res.json();
        const served = basename(json?.files?.["main.js"]);
        if (!served) return;

        if (served === running) return;

        // Detection is reported even when the banner will never render —
        // `suppressed` and dismissal are not consulted here on purpose. This
        // measures how many people are RUNNING stale code; app_update_prompted
        // measures how many were offered a fix. Own ref, so the two can't
        // interfere.
        if (!detectedRef.current) {
          detectedRef.current = true;
          captureUpdateDetected({ running, served });
        }

        // Functional set so a later check that finds the same staleness returns
        // the existing object instead of a new one — otherwise every focus past
        // the throttle would re-render the banner for no reason.
        if (!cancelled) setBundles((prev) => prev || { running, served });
      } catch {
        /* silent by design — see header */
      }
    };

    checkFreshness();

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkFreshness();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Prompted fires on VISIBILITY, not on detection: a tab that goes stale
  // mid-auction was never actually offered anything, and counting it would
  // inflate the prompt side of the funnel with prompts nobody saw.
  const visible = !!bundles && !dismissed && !suppressed;

  useEffect(() => {
    if (!visible || promptedRef.current) return;
    promptedRef.current = true;
    captureUpdatePrompted(bundles);
  }, [visible, bundles]);

  if (!visible) return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        // Below the modal layer (1000+) on purpose: a modal is a deliberate
        // interaction and must never be covered by an ambient notice.
        zIndex: 900,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.lg,
        padding: `${theme.spacing.lg}px ${theme.spacing.xl}px`,
        background: theme.colors.bg.elevated,
        borderTop: `1px solid ${theme.colors.border.brand}`,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.45)",
        fontFamily: theme.fonts.body,
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: theme.fontSize.body,
          color: theme.colors.text.primary,
        }}
      >
        A newer version is available.
      </span>

      <button
        type="button"
        onClick={() => {
          captureUpdateReloaded(bundles);
          window.location.reload();
        }}
        style={{
          flexShrink: 0,
          background: theme.colors.brand.gradientBtn,
          color: "#fff",
          border: "none",
          borderRadius: theme.radius.md,
          padding: "8px 18px",
          fontFamily: theme.fonts.display,
          fontSize: theme.fontSize.small,
          fontWeight: theme.fontWeight.heavy,
          letterSpacing: "1px",
          cursor: "pointer",
        }}
      >
        RELOAD
      </button>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: theme.colors.text.faint,
          fontSize: theme.fontSize.base,
          lineHeight: 1,
          padding: theme.spacing.xs,
        }}
      >
        ×
      </button>
    </div>
  );
}
